// src/components/admin/CollegeManagement.jsx
import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Import your configured API instance
import AdminCollegeForm from './AdminCollegeForm';

const CollegeManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);

  // Fetch colleges on load
  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      // Adjust endpoint to match your controller (e.g., /api/colleges or /api/admin/colleges)
      const response = await api.get('/colleges');
      setColleges(response.data);
    } catch (error) {
      console.error('Failed to load colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewClick = () => {
    setSelectedCollege(null);
    setIsEditing(true);
  };

  const handleEditClick = (college) => {
    setSelectedCollege(college);
    setIsEditing(true);
  };

  const handleFormSave = async (formData) => {
    try {
      if (selectedCollege) {
        // Update operational flow
        const response = await api.put(`/admin/colleges/${selectedCollege.id}`, formData);
        setColleges(colleges.map((c) => (c.id === selectedCollege.id ? response.data : c)));
        alert('College configuration updated.');
      } else {
        // Save execution flow
        const response = await api.post('/admin/colleges', formData);
        setColleges([...colleges, response.data]);
        alert('New institution added successfully.');
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Operation failed:', error);
      alert(error.response?.data?.message || 'Error processing request.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this college profile? This could break related merchandise products.')) {
      try {
        await api.delete(`/admin/colleges/${id}`);
        setColleges(colleges.filter((c) => c.id !== id));
        alert('College deleted successfully.');
      } catch (error) {
        console.error('Deletion failure:', error);
        alert('Could not execute delete. Ensure no active products are linked to this college.');
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">College Administration</h2>
          <p className="text-sm text-gray-500">Configure institutions and campus profile themes</p>
        </div>
        {!isEditing && (
          <button
            onClick={handleAddNewClick}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
          >
            <span className="text-lg">+</span> Add College
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="mb-6">
          <AdminCollegeForm
            college={selectedCollege}
            onSave={handleFormSave}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-500 font-medium">Loading asset profiles...</div>
      ) : colleges.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-xl text-gray-400">
          No registered colleges found. Click "Add College" to populate inventory tracking.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-gray-600 text-sm font-semibold">
                <th className="p-4 w-20">Logo</th>
                <th className="p-4">Institution Name</th>
                <th className="p-4 w-32">Code</th>
                <th className="p-4 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 text-gray-700">
              {colleges.map((college) => (
                <tr key={college.id} className="hover:bg-gray-50/50 transition">
                  <td className="p-4">
                    <img
                      src={college.logoUrl || 'https://placehold.co/50'}
                      alt={college.name}
                      className="w-10 h-10 object-contain bg-white rounded border p-1"
                      onError={(e) => { e.target.src = 'https://placehold.co/50?text=Error'; }}
                    />
                  </td>
                  <td className="p-4 font-medium text-gray-900">{college.name}</td>
                  <td className="p-4 text-gray-500 font-mono text-sm">{college.code}</td>
                  <td className="p-4 text-right space-x-3">
                    <button
                      onClick={() => handleEditClick(college)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(college.id)}
                      className="text-red-600 hover:text-red-800 font-medium text-sm transition"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CollegeManagement;